import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useBulkUpdateTransactions, useTransactions } from "../../api/queries";
import type {
  AttachmentId,
  EditActionDto,
  TransactionListDto,
} from "../../api/types";

export interface TransactionPickerDialogProps {
  open: boolean;
  attachmentId: AttachmentId | null;
  onClose: () => void;
  onLinked: () => void;
}

type AttachMode = "receipt" | "attachment";

export const TransactionPickerDialog = ({
  open,
  attachmentId,
  onClose,
  onLinked,
}: TransactionPickerDialogProps) => {
  const [merchantQuery, setMerchantQuery] = useState("");
  const [mode, setMode] = useState<AttachMode>("receipt");
  const [error, setError] = useState<string | undefined>();

  const { data: transactionPage, isLoading } = useTransactions({
    startDate: null,
    endDate: null,
    merchant: merchantQuery || null,
    limit: 25,
    cursor: null,
    sort: "dateDesc",
  });
  const transactions = transactionPage?.items;
  const bulkUpdate = useBulkUpdateTransactions();

  const handlePick = async (tx: TransactionListDto) => {
    if (!attachmentId) return;
    const action: EditActionDto =
      mode === "receipt"
        ? { field: "receipt", value: attachmentId }
        : { field: "addAttachment", value: attachmentId };
    try {
      await bulkUpdate.mutateAsync([{ id: tx.id, actions: [action] }]);
      onLinked();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to attach");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach to transaction</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <FormControl>
            <RadioGroup
              row
              value={mode}
              onChange={(_, v) => setMode(v as AttachMode)}
            >
              <FormControlLabel
                value="receipt"
                control={<Radio />}
                label="Set as receipt"
              />
              <FormControlLabel
                value="attachment"
                control={<Radio />}
                label="Add as attachment"
              />
            </RadioGroup>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search by merchant"
            value={merchantQuery}
            onChange={(e) => setMerchantQuery(e.target.value)}
          />
        </Stack>
        {isLoading ? (
          <Typography>Loading transactions…</Typography>
        ) : !transactions || transactions.length === 0 ? (
          <Typography
            sx={{
              color: "text.secondary",
            }}
          >
            No matching transactions.
          </Typography>
        ) : (
          <List dense>
            {transactions.map((tx) => (
              <ListItemButton
                key={tx.id}
                onClick={() => handlePick(tx)}
                disabled={bulkUpdate.isPending}
              >
                <ListItemText
                  primary={`${tx.datePaid} · ${tx.merchant}`}
                  secondary={`${tx.amount.amount} · ${tx.source}`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
